'use client';

import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useEffect, useState } from 'react';

import ContinueRegistrationV2 from './ContinueRegistrationV2';
import FinishRegistrationV2 from './FinishRegistrationV2';
import RegisterWithEmailFormV2 from './RegisterWithEmailFormV2';

import { AnalyticsParams, pushSimpleGAEvent } from '@components/Analytics/Google/GAScript';
import { useRouter } from '@lib/navigation';
import { fetchApi } from '@utils/api/client';
import { useUserStore } from '@utils/user';
import { langDropdownTypes } from 'types/lang';

type Step = 'register' | 'continue' | 'thankYou';

type FormProps = {
  onEmailAndConsentSubmitOverride?: any;
  onContinueSubmitOverride?: any;
  onContinueCancelOverride?: any;
  overrideStep?: Step;
  overrideIsConfirmed?: boolean;
  overrideEmail?: string;
  overrideRedirectUrl?: string;
  analyticsData: AnalyticsParams;
};

export default function RegisterFormV2({
  onEmailAndConsentSubmitOverride,
  onContinueSubmitOverride,
  onContinueCancelOverride,
  overrideStep,
  overrideIsConfirmed,
  overrideEmail,
  overrideRedirectUrl,
  analyticsData,
}: FormProps) {
  const [step, setStep] = useState<Step>('register');
  const [isShortForm, setShortForm] = useState(true);
  const userStore = useUserStore();
  const locale = useLocale();
  const [formData, setFormData] = useState<any | null>({ lang: locale });
  const [formDisabled, setFormDisabled] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [cancelClicked, setCancelClicked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(true);
  const router = useRouter();
  const t = useTranslations();

  const currentStep = overrideStep || step;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUrl = urlParams.get('from');
    if (redirectUrl) {
      setFormData({ ...formData, data: { redirectUrl } });
    }
  }, [typeof window]);

  const onEmailAndConsentSubmit = async (data: any, setError: (...data: any) => void) => {
    if (onEmailAndConsentSubmitOverride) {
      return onEmailAndConsentSubmitOverride(data, setError);
    }
    const response = await fetchApi('/auth/register/v2/check-email', {
      method: 'POST',
      onFailure: 'continue',
      body: JSON.stringify({ email: data.email }),
    });
    if (response?.status === 200) {
      setFormData({ ...formData, shortForm: true, ...data });
      setStep('continue');
      pushSimpleGAEvent('transinfo_register_step_1', {
        ...analyticsData,
        transinfo_is_logged_in: 'false',
        transinfo_is_newsletter_user: 'false',
        transinfo_user_email: data.email,
        transinfo_user_provider: 'wordpress',
        locale: locale,
        transinfo_view_category: 'register-main',
      });
    } else {
      setError('email', {
        type: 'manual',
        message: t('main.message.errors.emailExists'),
      });
    }
  };

  const onContinueSubmit = async (data: any) => {
    if (onContinueSubmitOverride) {
      return onContinueSubmitOverride(data);
    }
    setFormDisabled(true);
    setShortForm(false);
    const ultimateData = { ...formData, shortForm: false, ...data };
    setIsConfirmed(false);
    const respones = await fetchApi('/auth/register/v2', {
      method: 'POST',
      onFailure: 'continue',
      body: JSON.stringify(ultimateData),
    });
    setStep('thankYou');
    if (respones?.status === 201) {
      setIsSuccess(true);
      pushSimpleGAEvent('transinfo_register_step_2', {
        transinfo_is_logged_in: 'false',
        transinfo_is_newsletter_user: 'true',
        transinfo_user_email: formData.email,
        transinfo_user_provider: 'wordpress',
        locale: locale,
      });
    } else {
      setIsSuccess(false);
    }
    await userStore.fetchUser();
    setFormDisabled(false);
  };

  const onContinueCancel = async () => {
    if (onContinueCancelOverride) {
      return onContinueCancelOverride();
    }
    setCancelClicked(true);
    setFormDisabled(true);
    pushSimpleGAEvent('transinfo_register_step_2_cancel', {
      transinfo_is_logged_in: 'false',
      transinfo_is_newsletter_user: 'false',
      transinfo_user_email: formData.email,
      transinfo_user_provider: 'wordpress',
      locale: locale,
    });
    // register short form
    await fetchApi('/auth/register/v2', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
    await userStore.fetchUser();
    router.push(formData.data.redirectUrl || '/', {
      locale
    } as {
      locale: langDropdownTypes | undefined;
    });
    setFormDisabled(false);
  };

  return (
    <div className="flex flex-col bg-white p-4 lg:p-8 dark:bg-dark-500">
      <div className="mx-auto flex max-w-[460px] flex-col items-center justify-center">
        {currentStep === 'register' && (
          <RegisterWithEmailFormV2 onSubmit={onEmailAndConsentSubmit} className="flex flex-col" />
        )}
        {currentStep === 'continue' && (
          <ContinueRegistrationV2
            formDisabled={formDisabled}
            cancelClicked={cancelClicked}
            onSubmit={onContinueSubmit}
            onCancel={onContinueCancel}
          />
        )}
        {currentStep === 'thankYou' && (
          <FinishRegistrationV2
            isShort={isShortForm}
            isSuccess={isSuccess}
            email={formData.email || overrideEmail}
            isConfirmed={!!overrideIsConfirmed || isConfirmed}
            redirectUrl={formData?.data?.redirectUrl || overrideRedirectUrl}
          />
        )}
      </div>
    </div>
  );
}
